import { inject, Injectable, InjectionToken, Provider, Signal, signal, type Type } from '@angular/core';

export type ToastKind = 'success' | 'error' | 'warn' | 'info' | 'loading';

const DEFAULT_TIMEOUT_MS = 4000;
/** Сколько живёт обычный тост. Дефолт либы общий для всех приложений; продукт, где тосты читают,
 *  отвлекаясь от работы, задаёт своё значение провайдером — не правкой общего дефолта. */
export const HLM_TOAST_DEFAULT_TIMEOUT_MS = new InjectionToken<number>('HLM_TOAST_DEFAULT_TIMEOUT_MS', {
  providedIn: 'root',
  factory: () => DEFAULT_TIMEOUT_MS,
});

export function provideHlmToastDefaultTimeout(timeoutMs: number): Provider {
  return { provide: HLM_TOAST_DEFAULT_TIMEOUT_MS, useValue: timeoutMs };
}

const ERROR_TIMEOUT_MS = 8000;
// A warning survives a glance the way an error does: it reports a defect in output the user is about to print.
const WARN_TIMEOUT_MS = 8000;

export interface Toast {
  id: number;
  title: string;
  kind: ToastKind;
  /** Вторая строка под заголовком — объяснение к нему. Без неё тост остаётся однострочным. */
  detail?: string;
  /** Перечень под заголовком, по пункту в строку: кого или что тост называет. */
  items?: readonly string[];
  actionLabel?: string;
  /** Второй ответ рядом с основным (`choice`): тост с двумя путями, крестик закрывает, не выбирая ни один. */
  secondaryActionLabel?: string;
  closable?: boolean;
  /** Opt-in bottom-right column on a top host; every toast, undo included, defaults to the top. No caller sets it today. */
  position?: 'bottom';
  /** Компонент в строку заголовка, справа от текста: значки с числами, которые продукт уже рисует сам. */
  content?: ToastContent;
}

export interface ToastContent {
  readonly component: Type<unknown>;
  readonly inputs?: Record<string, unknown>;
}

export interface UndoToastOptions {
  kind?: ToastKind;
  /** Second line under the title — the toast's `detail`. */
  description?: string;
  actionLabel?: string;
  /** 0 — no timer: the toast stays until its action or `dismiss`. */
  timeoutMs?: number;
  /** Draws the cross. Only for a toast with no `onCommit`: the cross drops the toast, never commits. */
  closable?: boolean;
  onCommit?: () => void;
  onUndo?: () => void;
}

interface ToastHandlers {
  timer?: ReturnType<typeof setTimeout>;
  onAction?: () => void;
  onSecondaryAction?: () => void;
  keepOnAction?: boolean;
}

/** Один ответ тоста с выбором: надпись кнопки и что она делает. */
export interface ToastChoice {
  label: string;
  run: () => void;
}

/** Тост с выбором: тон, срок (0 — висит до ответа), перечень под заголовком и пояснение под перечнем. */
export interface ToastChoiceOptions {
  kind?: ToastKind;
  timeoutMs?: number;
  items?: readonly string[];
  detail?: string;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts: Signal<Toast[]> = this._toasts.asReadonly();

  private nextId = 0;
  private readonly handlers = new Map<number, ToastHandlers>();
  private readonly defaultTimeoutMs = inject(HLM_TOAST_DEFAULT_TIMEOUT_MS);

  /** `position: 'bottom'` = the opt-in bottom-right column (see `Toast.position`); omit it to land in the top column. */
  show(
    title: string,
    kind: ToastKind = 'info',
    timeoutMs?: number,
    detail?: string,
    position?: Toast['position'],
    content?: ToastContent,
    closable?: boolean,
  ): number {
    const id = ++this.nextId;
    const ms =
      timeoutMs ??
      (kind === 'error' ? ERROR_TIMEOUT_MS : kind === 'warn' ? WARN_TIMEOUT_MS : kind === 'loading' ? 0 : this.defaultTimeoutMs);
    this._toasts.update(list => [
      ...list,
      {
        id,
        title,
        kind,
        ...(detail ? { detail } : {}),
        ...(position ? { position } : {}),
        ...(content ? { content } : {}),
        ...(closable === undefined ? {} : { closable }),
      },
    ]);
    if (ms > 0) {
      this.handlers.set(id, { timer: setTimeout(() => this.dismiss(id), ms) });
    }

    return id;
  }

  success(title: string, detail?: string, content?: ToastContent): number {
    return this.show(title, 'success', undefined, detail, undefined, content);
  }

  error(title: string, detail?: string): number {
    return this.show(title, 'error', undefined, detail);
  }

  warn(title: string, detail?: string): number {
    return this.show(title, 'warn', undefined, detail);
  }

  info(title: string, detail?: string): number {
    return this.show(title, 'info', undefined, detail);
  }

  /** Ход долгой операции: крутится, пока вызывающий не снимет его `dismiss(id)` — сам не гаснет. */
  loading(title: string, detail?: string): number {
    return this.show(title, 'loading', 0, detail);
  }

  // Every action toast carries the cross and clears itself: a toast left up until closed piles into a stack.
  // `timeoutMs` 0 keeps it up until answered — only a prompt that must not be missed passes it.
  // `keepOnAction` = a standing report whose action only opens its details: the cross alone takes it down.
  action(
    title: string,
    actionLabel: string,
    onAction: () => void,
    kind: ToastKind = 'info',
    timeoutMs?: number,
    keepOnAction = false,
    detail?: string,
  ): number {
    const id = ++this.nextId;
    const ms = timeoutMs ?? (kind === 'error' ? ERROR_TIMEOUT_MS : this.defaultTimeoutMs);
    this._toasts.update(list => [...list, { id, title, kind, actionLabel, closable: true, ...(detail ? { detail } : {}) }]);
    this.handlers.set(id, {
      onAction,
      keepOnAction,
      ...(ms > 0 ? { timer: setTimeout(() => this.dismiss(id), ms) } : {}),
    });
    return id;
  }

  /**
   * Тост с двумя ответами: основной (акцентом) и второй; любой из них, как и крестик, закрывает тост. Основного нет
   * (`null`) — тост с одним ответом под текстом.
   */
  choice(title: string, primary: ToastChoice | null, secondary: ToastChoice, options: ToastChoiceOptions = {}): number {
    const { kind = 'info', timeoutMs, items, detail } = options;
    const id = ++this.nextId;
    const ms = timeoutMs ?? (kind === 'error' ? ERROR_TIMEOUT_MS : this.defaultTimeoutMs);
    this._toasts.update(list => [
      ...list,
      {
        id,
        title,
        kind,
        ...(primary ? { actionLabel: primary.label } : {}),
        secondaryActionLabel: secondary.label,
        closable: true,
        ...(items?.length ? { items } : {}),
        ...(detail ? { detail } : {}),
      },
    ]);
    this.handlers.set(id, {
      ...(primary ? { onAction: primary.run } : {}),
      onSecondaryAction: secondary.run,
      ...(ms > 0 ? { timer: setTimeout(() => this.dismiss(id), ms) } : {}),
    });
    return id;
  }

  /** Rewrites a live toast's title in place — a changing count updates its toast, never stacks a second one. */
  update(id: number, title: string): void {
    this._toasts.update(list => list.map(t => (t.id === id ? { ...t, title } : t)));
  }

  // Commits on timer expiry; the action button cancels the commit and runs onUndo instead. The timer IS the undo
  // window, so unlike an action toast it carries no cross by default: closing would strand the pending commit.
  showWithUndo(title: string, options: UndoToastOptions = {}): number {
    const id = ++this.nextId;
    const timeoutMs = options.timeoutMs ?? 3000;
    this._toasts.update(list => [
      ...list,
      {
        id,
        title,
        kind: options.kind ?? 'success',
        ...(options.description ? { detail: options.description } : {}),
        actionLabel: options.actionLabel ?? 'Восстановить',
        ...(options.closable ? { closable: true } : {}),
      },
    ]);
    this.handlers.set(id, {
      ...(timeoutMs > 0
        ? {
            timer: setTimeout(() => {
              this.remove(id);
              options.onCommit?.();
            }, timeoutMs),
          }
        : {}),
      onAction: options.onUndo,
    });
    return id;
  }

  runAction(id: number): void {
    const handlers = this.handlers.get(id);
    if (!handlers?.keepOnAction) this.remove(id);
    handlers?.onAction?.();
  }

  runSecondaryAction(id: number): void {
    const handlers = this.handlers.get(id);
    this.remove(id);
    handlers?.onSecondaryAction?.();
  }

  dismiss(id: number): void {
    this.remove(id);
  }

  private remove(id: number): void {
    const handlers = this.handlers.get(id);
    if (handlers?.timer) clearTimeout(handlers.timer);
    this.handlers.delete(id);
    this._toasts.update(list => list.filter(t => t.id !== id));
  }
}
